import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Warehouse,
  Plus,
  RefreshCw,
  MapPin,
  Phone,
  Edit,
  Trash2,
  MoreVertical,
  Building2,
  Loader2,
  Store,
  AlertTriangle,
  CheckCircle,
  Search
} from 'lucide-react';
import { Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import usePermissions from '@/hooks/usePermissions';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function WarehousesPage() {
  const { hasPermission, isAdmin } = usePermissions();
  const canEdit = isAdmin || hasPermission('inventory.warehouses.edit');
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    country: 'UAE',
    zip: '',
    phone: '',
    is_primary: false,
    express_enabled: false,
    express_sla_days: 0,
    express_sla_hours: 0,
    express_cities: '',
    express_cutoff_time: '',
    express_day_start: '09:00',
    express_off_days: []
  });

  const fetchLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/enterprise/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleSyncFromShopify = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/enterprise/locations/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Locations synced successfully');
      fetchLocations();
    } catch (error) {
      console.error('Error syncing locations:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync locations');
    } finally {
      setSyncing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address1: '',
      address2: '',
      city: '',
      province: '',
      country: 'UAE',
      zip: '',
      phone: '',
      is_primary: false,
      express_enabled: false,
      express_sla_days: 0,
      express_sla_hours: 0,
      express_cities: '',
      express_cutoff_time: '',
      express_day_start: '09:00',
      express_off_days: []
    });
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleOpenEdit = (location) => {
    setSelectedLocation(location);
    setFormData({
      name: location.name || '',
      address1: location.address1 || '',
      address2: location.address2 || '',
      city: location.city || '',
      province: location.province || '',
      country: location.country || 'UAE',
      zip: location.zip || '',
      phone: location.phone || '',
      is_primary: location.is_primary || false,
      express_enabled: !!location.express_enabled,
      express_sla_days: location.express_sla_days || 0,
      express_sla_hours: location.express_sla_hours || 0,
      express_cities: (location.express_cities || []).join(', '),
      express_cutoff_time: location.express_cutoff_time || '',
      express_day_start: location.express_day_start || '09:00',
      express_off_days: location.express_off_days || []
    });
    setShowEditModal(true);
  };

  const handleOpenDelete = (location) => {
    setSelectedLocation(location);
    setShowDeleteDialog(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    ...formData,
    express_sla_days: parseInt(formData.express_sla_days, 10) || 0,
    express_sla_hours: parseInt(formData.express_sla_hours, 10) || 0,
    express_cities: String(formData.express_cities || '').split(',').map(c => c.trim()).filter(Boolean),
    express_cutoff_time: formData.express_cutoff_time || null,
    express_day_start: formData.express_day_start || '09:00',
    express_off_days: formData.express_off_days || []
  });

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Location name is required');
      return;
    }

    setFormSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/enterprise/locations`,
        buildPayload(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Location created successfully');
      setShowAddModal(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      console.error('Error creating location:', error);
      toast.error(error.response?.data?.detail || 'Failed to create location');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Location name is required');
      return;
    }

    setFormSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/enterprise/locations/${selectedLocation.id}`,
        buildPayload(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Location updated successfully');
      setShowEditModal(false);
      setSelectedLocation(null);
      resetForm();
      fetchLocations();
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error(error.response?.data?.detail || 'Failed to update location');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setFormSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/api/enterprise/locations/${selectedLocation.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Location deleted successfully');
      setShowDeleteDialog(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Filter locations
  const filteredLocations = locations.filter(loc =>
    loc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.address1?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render form fields inline to prevent focus loss
  const renderFormFields = () => (
    <>
      <div>
        <Label className="text-gray-300">Location Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Main Warehouse"
          className="bg-gray-800/50 border-gray-700 text-white"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">Address Line 1</Label>
          <Input
            value={formData.address1}
            onChange={(e) => setFormData(prev => ({ ...prev, address1: e.target.value }))}
            placeholder="Street address"
            className="bg-gray-800/50 border-gray-700 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300">Address Line 2</Label>
          <Input
            value={formData.address2}
            onChange={(e) => setFormData(prev => ({ ...prev, address2: e.target.value }))}
            placeholder="Apt, suite, etc."
            className="bg-gray-800/50 border-gray-700 text-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">City</Label>
          <Input
            value={formData.city}
            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
            placeholder="City"
            className="bg-gray-800/50 border-gray-700 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300">State/Province</Label>
          <Input
            value={formData.province}
            onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
            placeholder="State or Province"
            className="bg-gray-800/50 border-gray-700 text-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">Country</Label>
          <Input
            value={formData.country}
            onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
            placeholder="Country"
            className="bg-gray-800/50 border-gray-700 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300">ZIP/Postal Code</Label>
          <Input
            value={formData.zip}
            onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
            placeholder="ZIP code"
            className="bg-gray-800/50 border-gray-700 text-white"
          />
        </div>
      </div>

      <div>
        <Label className="text-gray-300">Phone</Label>
        <Input
          value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          placeholder="Contact phone"
          className="bg-gray-800/50 border-gray-700 text-white"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_primary"
          checked={formData.is_primary}
          onChange={(e) => setFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
          className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500"
        />
        <Label htmlFor="is_primary" className="text-gray-300 cursor-pointer">
          Set as primary location
        </Label>
      </div>

      {/* Express shipping */}
      <div className="p-4 border border-amber-500/30 rounded-lg bg-amber-500/5" data-testid="warehouse-express-section">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-amber-400 font-medium flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Express Shipping
            </Label>
            <p className="text-xs text-gray-400 mt-1">
              Orders to the cities below (with express-eligible products) get flagged EXPRESS with a delivery countdown
            </p>
          </div>
          <input
            type="checkbox"
            checked={formData.express_enabled}
            onChange={(e) => setFormData(prev => ({ ...prev, express_enabled: e.target.checked }))}
            className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-amber-500"
            data-testid="warehouse-express-toggle"
          />
        </div>
        {formData.express_enabled && (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Deliver within (days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.express_sla_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, express_sla_days: e.target.value }))}
                  className="bg-gray-800/50 border-gray-700 text-white"
                  data-testid="express-sla-days-input"
                />
              </div>
              <div>
                <Label className="text-gray-300">+ hours</Label>
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={formData.express_sla_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, express_sla_hours: e.target.value }))}
                  className="bg-gray-800/50 border-gray-700 text-white"
                  data-testid="express-sla-hours-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Express cities (comma-separated)</Label>
              <Input
                value={formData.express_cities}
                onChange={(e) => setFormData(prev => ({ ...prev, express_cities: e.target.value }))}
                placeholder="e.g., Dubai, Sharjah, Abu Dhabi"
                className="bg-gray-800/50 border-gray-700 text-white"
                data-testid="express-cities-input"
              />
              <p className="text-xs text-gray-500 mt-1">Only orders shipping to these cities qualify (case-insensitive). Works per-country since each warehouse lists its own cities.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Order cutoff time</Label>
                <Input
                  type="time"
                  value={formData.express_cutoff_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, express_cutoff_time: e.target.value }))}
                  className="bg-gray-800/50 border-gray-700 text-white"
                  data-testid="express-cutoff-input"
                />
                <p className="text-xs text-gray-500 mt-1">Orders after this time start counting next working day. Empty = no cutoff</p>
              </div>
              <div>
                <Label className="text-gray-300">Day start time</Label>
                <Input
                  type="time"
                  value={formData.express_day_start}
                  onChange={(e) => setFormData(prev => ({ ...prev, express_day_start: e.target.value }))}
                  className="bg-gray-800/50 border-gray-700 text-white"
                  data-testid="express-daystart-input"
                />
                <p className="text-xs text-gray-500 mt-1">When the SLA clock starts on the next working day</p>
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Off days (no express processing)</Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="express-offdays-row">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                  const active = (formData.express_off_days || []).includes(idx);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        express_off_days: active
                          ? prev.express_off_days.filter(d => d !== idx)
                          : [...(prev.express_off_days || []), idx]
                      }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active
                        ? 'bg-red-500/20 border-red-500/60 text-red-400'
                        : 'bg-gray-800/50 border-gray-700 text-gray-400'}`}
                      data-testid={`express-offday-${day.toLowerCase()}`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1">Orders on off days (and deadlines landing on them) shift to the next working day</p>
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Warehouse className="w-7 h-7 text-purple-400" />
            Warehouses & Locations
          </h1>
          <p className="text-gray-400 mt-1">Manage your warehouse locations for inventory storage</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSyncFromShopify}
            disabled={syncing}
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            data-testid="sync-locations-btn"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Store className="w-4 h-4 mr-2" />
            )}
            Sync from Shopify
          </Button>
          {canEdit && (
            <Button
              onClick={handleOpenAdd}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              data-testid="add-location-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{locations.length}</p>
              <p className="text-sm text-gray-400">Total Locations</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Store className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {locations.filter(l => l.shopify_location_id).length}
              </p>
              <p className="text-sm text-gray-400">Shopify Synced</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {locations.filter(l => l.is_manual).length}
              </p>
              <p className="text-sm text-gray-400">Manual Locations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search locations..."
          className="pl-10 bg-gray-800/50 border-gray-700 text-white"
          data-testid="search-locations"
        />
      </div>

      {/* Locations Grid */}
      {filteredLocations.length === 0 ? (
        <div className="bg-gray-800/50 rounded-xl p-12 text-center border border-gray-700/50">
          <Warehouse className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            {searchQuery ? 'No locations found' : 'No locations yet'}
          </h3>
          <p className="text-gray-400 mb-4">
            {searchQuery
              ? 'Try adjusting your search'
              : 'Add your first warehouse location or sync from Shopify'
            }
          </p>
          {!searchQuery && (
            <div className="flex justify-center gap-3">
              <Button
                onClick={handleSyncFromShopify}
                variant="outline"
                className="border-green-500/50 text-green-400"
              >
                <Store className="w-4 h-4 mr-2" />
                Sync from Shopify
              </Button>
              <Button
                onClick={handleOpenAdd}
                className="bg-purple-500 hover:bg-purple-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Manually
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLocations.map((location) => (
            <div
              key={location.id}
              className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 hover:border-purple-500/30 transition-all"
              data-testid={`location-card-${location.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    location.shopify_location_id 
                      ? 'bg-green-500/20' 
                      : 'bg-blue-500/20'
                  }`}>
                    {location.shopify_location_id ? (
                      <Store className="w-5 h-5 text-green-400" />
                    ) : (
                      <Building2 className="w-5 h-5 text-blue-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      {location.name}
                      {location.is_primary && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
                          Primary
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {location.shopify_location_id ? 'Synced from Shopify' : 'Manual location'}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-gray-800 border-gray-700">
                      <DropdownMenuItem
                        onClick={() => handleOpenEdit(location)}
                        className="text-gray-300 hover:text-white hover:bg-gray-700"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleOpenDelete(location)}
                        className="text-red-400 hover:text-red-300 hover:bg-gray-700"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Address */}
              {(location.address1 || location.city) && (
                <div className="flex items-start gap-2 text-sm text-gray-400 mb-2">
                  <MapPin className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <span>
                    {[location.address1, location.city, location.province, location.country]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>
              )}

              {/* Phone */}
              {location.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>{location.phone}</span>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-700/50">
                {location.is_active !== false ? (
                  <span className="flex items-center gap-1 text-xs text-green-400">
                    <CheckCircle className="w-3 h-3" />
                    Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-red-400">
                    <AlertTriangle className="w-3 h-3" />
                    Inactive
                  </span>
                )}
                {location.express_enabled && (
                  <span className="flex items-center gap-1 text-xs text-amber-400" title={`Express: ${(location.express_cities || []).join(', ')} within ${location.express_sla_days || 0}d ${location.express_sla_hours || 0}h`} data-testid={`express-wh-badge-${location.id}`}>
                    <Zap className="w-3 h-3" />
                    Express
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Location Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-400" />
              Add New Location
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Create a new warehouse or storage location
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {renderFormFields()}
            <DialogFooter className="pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="border-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={formSubmitting}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                {formSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Create Location'
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Location Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-purple-400" />
              Edit Location
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Update location details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {renderFormFields()}
            <DialogFooter className="pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="border-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={formSubmitting}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                {formSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Delete Location
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete "{selectedLocation?.name}"? This action cannot be undone.
              {selectedLocation?.shopify_location_id && (
                <span className="block mt-2 text-yellow-400">
                  Note: This is a Shopify-synced location. Deleting it here will not remove it from Shopify.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={formSubmitting}
              className="bg-red-500 hover:bg-red-600"
            >
              {formSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
